import { cn } from '@/lib/utils';
import { FormFieldGroup, Input, RichTextEditor } from '@startupquickstart/frontend';
import { useState } from 'react';
import type { ChallengeEditorSectionProps } from '../types';
import { isAiValidatedAnswerType, requiresTextAnswer } from '../validation';
import { SectionCard } from './SectionCard';

function contentDescription(values: ChallengeEditorSectionProps['values']): string {
  if (isAiValidatedAnswerType(values.answerType)) {
    return 'The clue, hint, and AI validation criteria for photo/video submissions';
  }
  switch (values.answerType) {
    case 'true_false':
      return 'The clue, hint, and correct True/False answer';
    case 'multiple_choice':
      return 'The clue, hint, choices, and correct answer selection';
    case 'fill_in_the_blank':
      return 'The clue with ___ blanks, hint, and correct answer';
    default:
      return 'The clue, hint, and text answer for this challenge';
  }
}

/**
 * Clue, hint, hint cost, and the per-answer-type answer editor (all seven
 * answer kinds in both contexts). Event context adds the description field.
 */
export function ContentSection({
  values,
  errors,
  updateField,
  context,
}: ChallengeEditorSectionProps) {
  const isAi = isAiValidatedAnswerType(values.answerType);

  // Comma-separated view of the alternate answers. Local text state keeps
  // in-progress commas/spaces intact; the parsed array lives in `values`.
  // Sections mount only after the form is seeded, so seeding here is safe.
  const [alternateText, setAlternateText] = useState(() =>
    values.alternateAnswers.join(', '),
  );

  const onAlternateChange = (text: string) => {
    setAlternateText(text);
    updateField(
      'alternateAnswers',
      text
        .split(',')
        .map((a) => a.trim())
        .filter((a) => a.length > 0),
    );
  };

  return (
    <SectionCard id="content" title="Content" description={contentDescription(values)}>
      <FormFieldGroup name="clue" label="Clue" required error={errors.clue}>
        <RichTextEditor
          value={values.clue}
          onChange={(value) => updateField('clue', value ?? '')}
        />
        {values.answerType === 'fill_in_the_blank' && (
          <p className="mt-2 text-sm text-muted-foreground">
            Use <code className="font-mono font-semibold">___</code> (three underscores) to
            mark blank positions in the clue above.
          </p>
        )}
      </FormFieldGroup>

      <FormFieldGroup name="hint" label="Hint">
        <RichTextEditor
          value={values.hint}
          onChange={(value) => updateField('hint', value ?? '')}
        />
      </FormFieldGroup>

      <FormFieldGroup
        name="hintCost"
        label="Hint Cost"
        description="Points deducted when a participant uses the hint (0 for free hints)"
        error={errors.hintCost}
      >
        <Input
          id="hintCost"
          type="number"
          min={0}
          value={values.hintCost}
          onChange={(e) => updateField('hintCost', parseInt(e.target.value, 10) || 0)}
          placeholder="0"
        />
      </FormFieldGroup>

      {/* Answer editor — varies by answer type */}
      {isAi ? (
        <FormFieldGroup
          name="aiContext"
          label="AI Validation Criteria"
          description="Describe what the photo/video must show to be accepted (e.g., 'A photo showing a red door with the number 42')"
          required
          error={errors.aiContext}
        >
          <RichTextEditor
            value={values.aiContext}
            onChange={(value) => updateField('aiContext', value ?? '')}
          />
        </FormFieldGroup>
      ) : values.answerType === 'true_false' ? (
        <FormFieldGroup
          name="answer"
          label="Correct Answer"
          description="Select which answer is correct"
          required
          error={errors.answer}
        >
          <div className="flex gap-3">
            {(['true', 'false'] as const).map((val) => (
              <button
                key={val}
                type="button"
                onClick={() => updateField('answer', val)}
                className={cn(
                  'flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors',
                  values.answer === val
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-foreground hover:bg-muted',
                )}
              >
                {val === 'true' ? 'True' : 'False'}
              </button>
            ))}
          </div>
        </FormFieldGroup>
      ) : values.answerType === 'multiple_choice' ? (
        <FormFieldGroup
          name="choices"
          label="Answer Choices"
          description="Add at least 2 choices. Select the correct one."
          error={errors.choices}
        >
          <div className="space-y-2">
            {values.choices.map((choice, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => updateField('answer', choice)}
                  className={cn(
                    'h-5 w-5 shrink-0 rounded-full border-2 transition-colors',
                    values.answer === choice
                      ? 'border-primary bg-primary'
                      : 'border-border bg-card',
                  )}
                  title="Mark as correct answer"
                />
                <Input
                  value={choice}
                  onChange={(e) => {
                    const next = [...values.choices];
                    const wasCorrect = values.answer === choice;
                    next[idx] = e.target.value;
                    updateField('choices', next);
                    if (wasCorrect) updateField('answer', e.target.value);
                  }}
                  placeholder={`Choice ${idx + 1}`}
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={() => {
                    updateField(
                      'choices',
                      values.choices.filter((_, i) => i !== idx),
                    );
                    if (values.answer === choice) updateField('answer', '');
                  }}
                  className="text-muted-foreground hover:text-destructive transition-colors text-sm"
                  aria-label={`Remove choice ${idx + 1}`}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => updateField('choices', [...values.choices, ''])}
              className="text-sm text-primary hover:underline"
            >
              + Add choice
            </button>
          </div>
          {errors.answer && <p className="mt-1 text-sm text-destructive">{errors.answer}</p>}
        </FormFieldGroup>
      ) : requiresTextAnswer(values.type, values.answerType) ? (
        <>
          <FormFieldGroup name="answer" label="Answer" required error={errors.answer}>
            <Input
              id="answer"
              value={values.answer}
              onChange={(e) => updateField('answer', e.target.value)}
              placeholder="Enter the correct answer"
              className={cn(errors.answer && 'border-destructive')}
            />
          </FormFieldGroup>

          <FormFieldGroup
            name="alternateAnswers"
            label="Alternate Answers"
            description="Separate multiple answers with commas"
          >
            <Input
              id="alternateAnswers"
              value={alternateText}
              onChange={(e) => onAlternateChange(e.target.value)}
              placeholder="e.g., answer1, answer2, answer3"
            />
          </FormFieldGroup>

          <FormFieldGroup
            name="aiContext"
            label="AI Context"
            description="Optional: Additional context to help the AI validate answers"
          >
            <RichTextEditor
              value={values.aiContext}
              onChange={(value) => updateField('aiContext', value ?? '')}
            />
          </FormFieldGroup>
        </>
      ) : null}

      {context === 'event' && (
        <FormFieldGroup name="description" label="Description">
          <RichTextEditor
            value={values.description}
            onChange={(value) => updateField('description', value ?? '')}
          />
        </FormFieldGroup>
      )}
    </SectionCard>
  );
}
